import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { ACTIVE_ACCESS, TRIAL_URGENT_DAYS } from '../../../components/design-system/plan/access';
import { money } from './plan-ladder';

const formatDate = (iso: string | null, lang: string) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
};

/**
 * Where the vendor stands, in one line (docs/pricing-v2.md): a running trial
 * and its end, a trial or subscription that ended, or the agreement it is on.
 * Nothing for a vendor paying month to month.
 */
export function AccessBanner({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const plan = useVendorPlan();
  // Read loosely: a mocked or older hook may not carry the pricing v2 fields.
  const access = plan.access ?? ACTIVE_ACCESS;
  const isTrial = !!plan.isTrial;
  const contract = plan.contract ?? null;

  let tone: 'urgent' | 'calm' = 'calm';
  let title: string | null = null;
  let body: string | null = null;

  if (access.state === 'expired') {
    tone = 'urgent';
    title = access.reason === 'subscription_ended' ? t('PRICING.BANNER.SUB_ENDED_TITLE') : t('PRICING.BANNER.TRIAL_ENDED_TITLE');
    body = t('PRICING.BANNER.ENDED_BODY');
  } else if (isTrial) {
    const days = access.daysLeft;
    tone = days !== null && days <= TRIAL_URGENT_DAYS ? 'urgent' : 'calm';
    title = days === null
      ? t('PRICING.BANNER.TRIAL_PENDING_TITLE')
      : t('PRICING.BANNER.TRIAL_TITLE', { count: days, date: formatDate(access.trialEndsAt, i18n.language) });
    body = t('PRICING.BANNER.TRIAL_BODY');
  } else if (contract) {
    title = t('PRICING.BANNER.CONTRACT_TITLE', {
      count: contract.termMonths,
      amount: money(contract.monthlyAmount, contract.currency),
    });
    body = contract.endDate
      ? contract.autoRenew
        ? t('PRICING.BANNER.CONTRACT_RENEWS', { date: formatDate(contract.endDate, i18n.language), days: contract.noticeDays })
        : t('PRICING.BANNER.CONTRACT_ENDS', { date: formatDate(contract.endDate, i18n.language) })
      : null;
  }

  if (!title) return null;
  return (
    <div
      data-testid="access-banner"
      data-state={access.state}
      className={cn(
        'flex items-start gap-3 rounded-xl border bg-card p-4',
        tone === 'urgent' && 'border-foreground',
        className,
      )}
    >
      <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', tone === 'urgent' ? 'bg-destructive' : access.state === 'active' ? 'bg-muted-foreground' : 'bg-success')} aria-hidden />
      <div className="min-w-0">
        <p className="text-sm font-medium">{title}</p>
        {body && <p className="mt-0.5 text-sm text-muted-foreground">{body}</p>}
      </div>
    </div>
  );
}
